/**
 * STUDENT account statement (F3-H): dual USD/VES debt, invoices with status,
 * online payment button (Stripe checkout) and their payment history with a link to the
 * receipt. All server-only (REST); the student only sees their own data.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CreditCard, Receipt } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { Money } from "./money";
import { PrepayCard } from "./prepay-card";
import { receiptUrl, useCheckout, useMeInvoices, useMePayments, useMeSummary } from "../api";

const invoiceBadgeClass = (status: string): string => {
  if (status === "paid") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (status === "partial") {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-secondary/20";
};

export const StudentPayments: Component = () => {
  const { t } = useI18n();
  const summary = useMeSummary();
  const invoices = useMeInvoices();
  const payments = useMePayments();
  const checkout = useCheckout();

  const payOnline = () => {
    const open = (invoices.data ?? []).filter((i) => i.status === "open" || i.status === "partial");
    if (open.length === 0) {
      showToast(t(msg`Nothing to pay`), "info");
      return;
    }
    checkout.mutate(
      { invoiceIds: open.map((i) => i.id) },
      {
        onSuccess: (res) => {
          globalThis.location.href = res.checkoutUrl;
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="space-y-4">
      {/* Deuda */}
      <div class="rounded-xl border border-border p-4" data-testid="debt-card">
        <h2 class="mb-1 text-sm text-muted-foreground">{t(msg`Outstanding balance`)}</h2>
        <Show when={!summary.isLoading} fallback={<Skeleton class="h-10 w-40" />}>
          <p class="text-3xl font-extrabold">
            <Money
              usdMinor={summary.data?.totalDebtUsdMinor ?? 0}
              vesMinor={summary.data?.totalDebtVesMinor}
              vesPerUsd={summary.data?.vesRate}
            />
          </p>
          <Show when={(summary.data?.overdueInvoiceCount ?? 0) > 0}>
            <p class="mt-1 text-sm text-red-600" data-testid="overdue-badge">
              {t(msg`Overdue invoices`)}: {summary.data?.overdueInvoiceCount}
            </p>
          </Show>
          <Show when={summary.data?.nextDueDate}>
            <p class="mt-1 text-xs text-muted-foreground">
              {t(msg`Next due`)}: {summary.data?.nextDueDate}
            </p>
          </Show>
        </Show>
        <div class="mt-3">
          <Button onClick={payOnline} disabled={checkout.isPending} data-testid="pay-online">
            <CreditCard class="mr-1 h-4 w-4" />
            {t(msg`Pay online`)}
          </Button>
        </div>
      </div>

      {/* B4 · G3 — "Adelanta y ahorra", right below the debt: it's the moment
          when the student is looking at how much they owe. */}
      <PrepayCard vesPerUsd={summary.data?.vesRate} />

      {/* Facturas */}
      <div class="rounded-xl border border-border p-4">
        <h3 class="mb-2 font-semibold">{t(msg`Invoices`)}</h3>
        <Show when={!invoices.isLoading} fallback={<Skeleton class="h-24 w-full" />}>
          <Show
            when={(invoices.data?.length ?? 0) > 0}
            fallback={<p class="text-sm text-muted-foreground">{t(msg`No invoices`)}</p>}
          >
            <ul class="divide-y divide-border" data-testid="student-invoices">
              <For each={invoices.data}>
                {(inv) => (
                  <li class="flex items-center justify-between py-2 text-sm">
                    <span>
                      {inv.concept} {inv.seqLabel ? `· ${inv.seqLabel}` : ""}
                      <Show when={inv.overdue}>
                        <span class="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                          {t(msg`overdue`)}
                        </span>
                      </Show>
                    </span>
                    <Money
                      usdMinor={inv.remainingUsdMinor}
                      vesMinor={inv.remainingVesMinor}
                      showRate={false}
                    />
                    <span
                      class={`rounded-full px-2 py-0.5 text-xs ${invoiceBadgeClass(inv.status)}`}
                      data-testid={`invoice-status-${inv.id}`}
                    >
                      {inv.status}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>

      {/* Historial de pagos */}
      <div class="rounded-xl border border-border p-4">
        <h3 class="mb-2 font-semibold">{t(msg`Payment history`)}</h3>
        <Show when={!payments.isLoading} fallback={<Skeleton class="h-20 w-full" />}>
          <ul class="divide-y divide-border" data-testid="student-payments">
            <For each={payments.data}>
              {(p) => (
                <li class="flex items-center justify-between py-2 text-sm">
                  <Money usdMinor={p.amountUsdMinor} vesMinor={p.amountVesMinor} showRate={false} />
                  <span class="text-xs text-muted-foreground">{p.methodKey}</span>
                  <span class="text-xs">{p.status}</span>
                  <Show when={p.status === "verified"}>
                    <a
                      href={receiptUrl(p.id)}
                      target="_blank"
                      rel="noopener"
                      class="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Receipt class="h-3 w-3" />
                      {t(msg`Receipt`)}
                    </a>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </div>
  );
};
